import { Stack } from '@mui/material';
import useResponsive from '@/hooks/useResponsive';
import Typography from '@/components/Typography';
import Button from '@/components/Button';
import Image from 'next/image';
import React, { memo } from 'react';
import { useRouter } from 'next/navigation';
import useLiveCrypto, { sourceType } from '@/hooks/useLiveCrypto';

type CustomRowProps = {
  crypto?: {
    logo: string;
    nameFa: string;
    symbol: string;
  };
  source: sourceType;
  symbol: string;
  closePrice?: string;
  marketcap?: string;
  changePrice?: string;
  volume?: string;
  chart?: string;
  pingPong: boolean;
  onClickRedirect?: string;
  button?: { text: string; href: string; secondary?: string };
};

const CustomRow = ({
  crypto,
  closePrice,
  changePrice,
  volume,
  marketcap,
  chart,
  symbol,
  source,
  onClickRedirect,
  button,
  pingPong
}: CustomRowProps) => {
  const router = useRouter();
  const { matches } = useResponsive();
  const { message, isConnected } = useLiveCrypto({
    source,
    symbol,
    connect: true,
    pingPong: !!pingPong
  });

  const priceKey = source === 'otc' ? 'midPrice' : 'price';

  const handleColorChart = (change: number) => {
    if (change < 0)
      return 'hue-rotate(300deg) saturate(210%) brightness(1) contrast(170%)';
    if (change > 0) return 'hue-rotate(85deg) saturate(80%) brightness(1)';
    return 'hue-rotate(85deg) saturate(8%) brightness(1)';
  };

  const handleColorText = (change: number) => {
    if (change < 0) return 'error.A100';
    if (change > 0) return 'success.A100';
    return 'textColor.A30';
  };

  const change =
    isConnected && !!message?.priceChange ? message.priceChange : changePrice;
  return (
    <>
      {crypto && (
        <Stack
          gridArea="crypto"
          flexDirection="row"
          alignItems="center"
          gap={1.5}
          {...(onClickRedirect && {
            sx: { cursor: 'pointer' },
            onClick: () => router.push(onClickRedirect.toLowerCase())
          })}
        >
          <Image
            src={crypto.logo}
            alt=""
            width={32}
            height={32}
            style={{ borderRadius: '50%' }}
          />

          <Stack gap={0} maxWidth={70}>
            <Typography
              color="textColor.A30"
              variant="body2"
              weight="bold"
              noWrap
            >
              {crypto.nameFa}
            </Typography>
            <Typography color="textColor.A30" variant="body3" noWrap>
              {crypto.symbol}
            </Typography>
          </Stack>
        </Stack>
      )}
      {closePrice && (
        <Stack
          justifySelf="stretch"
          gridArea="closePrice"
          flexDirection="row"
          alignItems="center"
          gap={1}
          {...(onClickRedirect && {
            sx: { cursor: 'pointer' },
            onClick: () => router.push(onClickRedirect.toLowerCase())
          })}
        >
          <Typography
            color="textColor.A30"
            variant="body2"
            weight="bold"
            noWrap
            maxWidth={155}
            dir="ltr"
            textAlign="end"
          >
            {isConnected && !!message?.[priceKey]
              ? message[priceKey]
              : closePrice}
          </Typography>
        </Stack>
      )}
      {changePrice && (
        <Stack
          justifyContent={matches({ md: 'end', lg: 'start' })}
          justifySelf="stretch"
          gridArea="changePrice"
          flexDirection="row"
          alignItems="center"
          gap={1}
          {...(onClickRedirect && {
            sx: { cursor: 'pointer' },
            onClick: () => router.push(onClickRedirect.toLowerCase())
          })}
        >
          <Typography
            dir="ltr"
            color={handleColorText(Number(change || 0))}
            variant="body2"
            weight="bold"
          >
            {change}
          </Typography>
        </Stack>
      )}
      {volume && (
        <Stack
          justifySelf="stretch"
          gridArea="volume"
          flexDirection="row"
          alignItems="center"
          gap={1}
          {...(onClickRedirect && {
            sx: { cursor: 'pointer' },
            onClick: () => router.push(onClickRedirect.toLowerCase())
          })}
        >
          <Typography color="textColor.A30" variant="body2" weight="bold">
            {volume}
          </Typography>
        </Stack>
      )}
      {marketcap && (
        <Stack
          justifySelf="stretch"
          gridArea="marketcap"
          flexDirection="row"
          alignItems="center"
          gap={1}
          {...(onClickRedirect && {
            sx: { cursor: 'pointer' },
            onClick: () => router.push(onClickRedirect.toLowerCase())
          })}
        >
          <Typography color="textColor.A30" variant="body2" weight="bold">
            {marketcap}
          </Typography>
        </Stack>
      )}
      {chart && (
        <Stack
          justifySelf="stretch"
          justifyContent="center"
          gridArea="chart"
          flexDirection="row"
          alignItems="center"
          gap={1}
          {...(onClickRedirect && {
            sx: { cursor: 'pointer' },
            onClick: () => router.push(onClickRedirect.toLowerCase())
          })}
        >
          <Image
            src={chart}
            alt={crypto?.symbol || ''}
            width={135}
            height={28}
            style={{
              filter: handleColorChart(Number(changePrice || 0))
            }}
          />
        </Stack>
      )}
      {button && (
        <Stack
          justifySelf="end"
          gridArea="action"
          flexDirection="row"
          alignItems="center"
          gap={2}
        >
          {button.secondary && (
            <a href={button.secondary.toLowerCase()}>
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={theme => ({
                  background: theme.palette.primary.A10,
                  cursor: 'pointer',
                  width: 36,
                  height: 32,
                  borderRadius: 2
                })}
              >
                <Image
                  src="/assets/ChartBar.svg"
                  width={20}
                  height={20}
                  alt={`ارز قیمت ${crypto?.symbol}`}
                />
              </Stack>
            </a>
          )}
          <a href={button.href.toLowerCase()} target="_blank">
            <Button size="small" color="primary" sx={{ height: 32 }}>
              {button.text}
            </Button>
          </a>
        </Stack>
      )}
    </>
  );
};

export default memo(CustomRow);
